<script lang="ts">
  import type { Member } from '@clinic/contracts';
  import { toPersianDigits } from '@clinic/money-date';
  import { Check } from '@lucide/svelte';
  import { createMeQuery } from '../../../shared/auth/auth.svelte';
  import { useOrg } from '../../../shared/auth/org.svelte';
  import { createSaveSectionSettings, createSectionSettings } from '../../../shared/auth/sections';
  import { fa } from '../../../shared/i18n/fa';
  import { cn } from '../../../shared/lib/cn';
  import { focusOnMount } from '../../../shared/lib/focus';
  import { online } from '../../../shared/lib/online.svelte';
    import { Alert, Badge, Button, ErrorAlert, LoadingState } from '../../../shared/ui';
  import { AddMemberForm } from '../../members';
  import { WorkingHoursCard } from '../../settings';
  import { useCompleteWelcome } from '../api/welcome-api';
  import { tourSteps, welcomePages } from '../lib/tour';

  /**
   * The welcome (employer request 2026-10-12): a full-screen sheet on a phone and a centred panel on
   * a desktop. The clinic admin first sets the clinic up (laser, team, working hours; every step
   * skippable and saved at once); the last button then starts the tour of the real pages (employer
   * request 2026-10-13), which the gate runs and which marks the welcome done when it ends. «بعداً»
   * and Escape end it at once; the server remembers it so it does not come back.
   * An open <dialog> also keeps the PWA from reloading the page under the member (isBusy).
   */
  let { forced, onClose, onStartTour }: { forced: boolean; onClose: () => void; onStartTour: () => void } = $props();

  const org = useOrg();
  const me = createMeQuery();
  const complete = useCompleteWelcome(() => org.orgId);
  const pages = $derived(welcomePages(org.can, { setup: forced }));
  const hasTour = $derived(tourSteps(org.can).length > 0);
  let index = $state(0);
  const page = $derived(pages[Math.min(index, pages.length - 1)]!);
  const last = $derived(index >= pages.length - 1);

  let dialog = $state<HTMLDialogElement>();
  $effect(() => {
    if (dialog && !dialog.open) dialog.showModal();
  });

  // What was done in the setup, to tell «رد کردن» from «بعدی».
  let laserDone = $state(false);
  let hoursDone = $state(false);
  let added = $state<Member[]>([]);
  let adding = $state(true);
  let formKey = $state(0);

  const sections = createSectionSettings(() => org.orgId);
  const saveSections = createSaveSectionSettings(() => org.orgId);
  const laserOn = $derived(sections.data?.laserEnabled ?? false);

  function chooseLaser(on: boolean) {
    const s = sections.data;
    if (!s) return;
    saveSections.mutate(
      { laserEnabled: on, laserDeskRoles: s.laserDeskRoles, laserCashRoles: s.laserCashRoles },
      { onSuccess: () => (laserDone = true) },
    );
  }

  // The welcome is shown once: the server records it, then the sheet closes. A replay of an
  // already-welcomed member only closes. If the request fails the sheet stays and shows why.
  let finishing = false;
  function finish() {
    finishing = true;
    if (!forced) onClose();
    else complete.mutate(undefined, { onSuccess: onClose });
  }

  const roleNames = $derived(org.membership.roles.map((r) => fa.roles[r]));
  const stepDone = $derived(
    page.kind === 'setup' ? (page.id === 'laser' ? laserDone : page.id === 'members' ? added.length > 0 : hoursDone) : true,
  );
  const dots = $derived(pages.map((_, i) => i));
</script>

<dialog
  bind:this={dialog}
  aria-labelledby="welcome-title"
  data-testid="welcome-tour"
  oncancel={(e) => {
    // A replay closes like any dialog (the browser closes it, `close` reports it). The first welcome
    // stays open until the server has recorded it; nothing is closed from code meanwhile.
    if (!forced) return;
    e.preventDefault();
    if (!complete.isPending) finish();
  }}
  onclose={() => {
    if (!finishing && !dialog?.open) finish();
  }}
  class="m-auto h-dvh max-h-dvh w-full max-w-none overflow-hidden rounded-none border border-border bg-surface p-0 text-text backdrop:bg-[var(--scrim)] sm:h-auto sm:max-h-[90dvh] sm:w-[min(92vw,560px)] sm:rounded-xl"
>
  <div class="flex h-dvh max-h-dvh flex-col sm:h-auto sm:max-h-[90dvh]">
    <header class="flex items-center justify-between gap-3 border-b border-hairline px-4 py-3">
      <div class="min-w-0">
        <p class="text-xs text-text-3">{fa.welcomeTour.title}</p>
        {#if pages.length > 1}
          <p class="text-sm font-medium" aria-live="polite">
            {fa.welcomeTour.step(toPersianDigits(Math.min(index, pages.length - 1) + 1), toPersianDigits(pages.length))}
          </p>
        {/if}
      </div>
      <Button variant="ghost" size="sm" onclick={() => finish()} loading={complete.isPending} done={complete.isSuccess}
        >{fa.welcomeTour.later}</Button
      >
    </header>

    <div class="flex-1 overflow-y-auto px-4 py-5">
      {#key index}
        {#if page.kind === 'welcome'}
          <h2 id="welcome-title" tabindex="-1" use:focusOnMount={true} class="text-xl font-bold outline-none">
            {fa.welcomeTour.welcome.hello(me.data?.user.displayName ?? '')}
          </h2>
          <p class="mt-1 text-text-2">{fa.welcomeTour.welcome.clinic(org.membership.organization.name)}</p>
          <p class="mt-4 text-sm text-text-3">{fa.welcomeTour.welcome.roles}</p>
          <div class="mt-1.5 flex flex-wrap gap-2">
            {#each roleNames as name (name)}<Badge>{name}</Badge>{/each}
          </div>
          <p class="mt-5 text-sm text-text-2">{fa.welcomeTour.welcome.intro}</p>
          {#if forced && pages.some((p) => p.kind === 'setup')}
            <p class="mt-2 text-sm text-text-2">{fa.welcomeTour.welcome.adminIntro}</p>
          {/if}
        {:else if page.kind === 'setup' && page.id === 'laser'}
          <h2 id="welcome-title" tabindex="-1" use:focusOnMount={true} class="text-lg font-bold outline-none">
            {fa.welcomeTour.laser.title}
          </h2>
          <p class="mt-2 text-sm text-text-2">{fa.welcomeTour.laser.hint}</p>
          {#if sections.isPending}
            <LoadingState />
          {:else}
            <fieldset class="mt-4 min-w-0" disabled={!online.value || saveSections.isPending}>
              <legend class="sr-only">{fa.welcomeTour.laser.group}</legend>
              <div class="flex flex-wrap gap-2">
                {#each [true, false] as on (on)}
                  <label class="cursor-pointer">
                    <input
                      type="radio"
                      name="welcome-laser"
                      class="peer sr-only"
                      checked={laserDone && laserOn === on}
                      onchange={() => chooseLaser(on)}
                    />
                    <span
                      class="flex min-h-11 items-center rounded-md border border-border px-4 text-sm peer-checked:border-accent peer-checked:bg-accent-soft peer-checked:font-semibold peer-checked:text-accent peer-focus-visible:outline-2 peer-focus-visible:outline-accent"
                      >{on ? fa.welcomeTour.laser.yes : fa.welcomeTour.laser.no}</span
                    >
                  </label>
                {/each}
              </div>
            </fieldset>
            <div aria-live="polite" class="mt-3 empty:hidden">{#if laserDone}<Alert tone="success">{laserOn ? fa.welcomeTour.laser.savedYes : fa.welcomeTour.laser.savedNo}</Alert>{/if}</div>
            {#if saveSections.error}<div class="mt-3"><ErrorAlert error={saveSections.error} /></div>{/if}
            {#if laserOn}<p class="mt-3 text-sm text-text-3">{fa.welcomeTour.laser.more}</p>{/if}
          {/if}
        {:else if page.kind === 'setup' && page.id === 'members'}
          <h2 id="welcome-title" tabindex="-1" use:focusOnMount={true} class="text-lg font-bold outline-none">
            {fa.welcomeTour.members.title}
          </h2>
          <p class="mt-2 mb-4 text-sm text-text-2">{fa.welcomeTour.members.hint}</p>
          {#if added.length > 0}
            <p class="mb-1.5 text-sm font-medium text-text-2">{fa.welcomeTour.members.added}</p>
            <ul class="mb-4 flex flex-col divide-y divide-hairline rounded-md border border-border" data-testid="welcome-added">
              {#each added as m (m.id)}
                <li class="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <Check class="size-4 text-success" aria-hidden="true" />
                  <span class="font-medium">{m.user.displayName}</span>
                  <span class="text-text-3">{m.roles.map((r) => fa.roles[r]).join('، ')}</span>
                </li>
              {/each}
            </ul>
          {/if}
          {#if adding}
            {#key formKey}
              <AddMemberForm
                orgId={org.orgId}
                orgType="clinic"
                onAdded={(m) => {
                  added = [...added, m];
                  adding = false;
                }}
                onCancel={() => (adding = false)}
              />
            {/key}
          {:else}
            <Button
              variant="secondary"
              onclick={() => {
                formKey += 1;
                adding = true;
              }}>{added.length > 0 ? fa.welcomeTour.members.addAnother : fa.welcomeTour.members.add}</Button
            >
          {/if}
          <p class="mt-3 text-sm text-text-3">{fa.welcomeTour.members.later}</p>
        {:else if page.kind === 'setup'}
          <h2 id="welcome-title" tabindex="-1" use:focusOnMount={true} class="text-lg font-bold outline-none">
            {fa.welcomeTour.hours.title}
          </h2>
          <div class="mt-2"><WorkingHoursCard embedded onSaved={() => (hoursDone = true)} /></div>
        {/if}
      {/key}
      {#if complete.error}<div class="mt-4"><ErrorAlert error={complete.error} /></div>{/if}
    </div>

    <footer class="border-t border-hairline px-4 py-3">
      {#if pages.length > 1}
        <div class="mb-3 flex justify-center gap-1.5" aria-hidden="true">
          {#each dots as i (i)}
            <span class={cn('h-1.5 rounded-full', i === index ? 'w-5 bg-accent-solid' : 'w-1.5 bg-border-strong')}></span>
          {/each}
        </div>
      {/if}
      <div class="flex items-center justify-between gap-2">
        <Button variant="ghost" onclick={() => (index -= 1)} disabled={index === 0}>{fa.welcomeTour.back}</Button>
        {#if last}
          {#if hasTour}
            <Button onclick={onStartTour}>{fa.welcomeTour.startTour}</Button>
          {:else}
            <Button onclick={() => finish()} loading={complete.isPending} done={complete.isSuccess} disabled={!online.value && forced}>{fa.welcomeTour.finish}</Button>
          {/if}
        {:else}
          <Button
            variant={page.kind === 'setup' && !stepDone ? 'secondary' : 'primary'}
            onclick={() => (index += 1)}>{page.kind === 'setup' && !stepDone ? fa.welcomeTour.skipStep : fa.welcomeTour.next}</Button
          >
        {/if}
      </div>
    </footer>
  </div>
</dialog>
